<template>
  <router
    :show-unread-view="showUnreadView"
    :show-campaign-view="showCampaignView"
    :is-mobile="isMobile"
    :has-fetched="hasFetched"
    :unread-message-count="unreadMessageCount"
    :is-left-aligned="isLeftAligned"
    :hide-message-bubble="hideMessageBubble"
    :show-popout-button="showPopoutButton"
  />
</template>

<script>
import availabilityMixin from 'widget/mixins/availability';
import { mapGetters, mapActions } from 'vuex';
import { setConversationToken } from '../utils';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';
import Router from './views/Router';
import { getLocale } from './helpers/urlParamsHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CONVERSATION_STATUS_TYPES, CAMPAIGN_TRIGGER } from 'shared/constants/conversation';
import { isEmptyObject } from 'widget/helpers/utils';
import configMixin from './mixins/configMixin';
import chatBotsMixin from './mixins/chatBotsMixin';
import { handleLiveChatGainSight, LiveChatGainSightEventMap, sendGainSightEvent, SURFACE_KB_IN_CHAT_GS_EVENTS } from '../gainSightIntegration';
import { TAB_TYPE } from './helpers/constants';
import { loggerInstance } from '../utils/hiver-logs';
import HiverApi from 'widget/api/hiver';
import { sendExceptionToSentry } from 'shared/helpers/sentry';

export default {
  name: 'App',
  components: {
    Router
  },
  mixins: [availabilityMixin, configMixin, chatBotsMixin],
  data() {
    return {
      showUnreadView: false,
      showCampaignView: false,
      isMobile: false,
      hideMessageBubble: false,
      widgetPosition: 'right',
      showPopoutButton: false,
      isWebWidgetTriggered: false,
      isWidgetFirstOpening: true,
    };
  },
  computed: {
    ...mapGetters({
      activeCampaign: 'campaign/getActiveCampaign',
      authToken: 'appConfig/getAuthToken',
      campaigns: 'campaign/getCampaigns',
      contact: 'appConfig/getContact',
      currentUser: 'contacts/getCurrentUser',
      conversationAttributes: 'conversationAttributes/getConversationParams',
      conversationSize: 'conversation/getConversationSize',
      hasFetched: 'agent/getHasFetched',
      isWidgetConfigured: 'appConfig/getWidgetConfigured',
      messageCount: 'conversation/getMessageCount',
      unreadMessageCount: 'conversation/getUnreadMessageCount',
      webChannelConfig: 'appConfig/getWebChannelConfig'
    }),
    isLeftAligned() {
      const isLeft = this.widgetPosition === 'left';
      return isLeft;
    },
    isIFrame() {
      return IFrameHelper.isIFrame();
    },
    isRNWebView() {
      return RNHelper.isRNWebView();
    },
    isSurfaceKbEnabled() {
      return this.isSurfaceKBInLiveChatEnabled;
    },
  },
  watch: {
    activeCampaign() {
      this.setCampaignView();
    },
    async isWidgetConfigured() {
      const { websiteToken, locale } = this.webChannelConfig;
      await this.fetchHiverConfig(websiteToken);
      this.setLocale(locale);
      localStorage.setItem('locale', locale);
      
      //Check if the script has not been loaded on the webpage according to the server config
      // If script_loaded_on_webpage is false, notify the server that the script has now loaded i.e updating script_loaded_on_webpage to true
      if (this.hiverConfig.script_loaded_on_webpage === false) {
        await this.notifyScriptLoaded(websiteToken);
      }
      
      if (this.isIFrame) {
        this.registerListeners();
        this.sendLoadedEvent();
        setConversationToken(this.authToken);
      } else {
        setConversationToken(this.authToken);
        this.fetchOldConversations();
        this.fetchAvailableAgents(websiteToken);
        this.setLocale(getLocale(window.location.search));
      }
      if (this.isRNWebView) {
        this.registerListeners();
        this.sendRNWebViewLoadedEvent();
      }
      this.$store.dispatch('conversationAttributes/getAttributes').then(() => this.setupWidgetBeforeFirstOpeningIfConversationIsOpen());
      this.registerUnreadEvents();
      this.registerCampaignEvents();
    },
    currentUser( data ) {
      if( !data.email ){
        return;
      }
      loggerInstance.addMeta({ email: data.email });
    }
  },
  mounted() {
    const url = new URL(window.location.href);
    const conversationToken = url.searchParams.get('cw_conversation');
    setConversationToken(conversationToken);
    localStorage.setItem('websiteToken', url.searchParams.get('website_token'));
    this.$store.dispatch('appConfig/fetchAppConfig', {
      token: url.searchParams.get('cw_conversation'),
      websiteToken: url.searchParams.get('website_token')
    }).then(() => { this.setupWidget() });
    window.addEventListener('online', () => {
      this.fetchOldConversations();
      this.$store.dispatch('conversationAttributes/getAttributes');
    });
  },
  methods: {
    ...mapActions('conversation', ['clearConversations', 'fetchOldConversations', 'setUserLastSeen', 'setConversationsListLoading', 'sendMessage', 'registerChatsbotService',]),
    ...mapActions('conversationAttributes', ['clearConversationAttributes']),
    ...mapActions('campaign', ['initCampaigns', 'executeCampaign']),
    ...mapActions('agent', ['fetchAvailableAgents']),
    ...mapActions('hiverConfig', ['fetchHiverConfig']),
    scrollConversationToBottom() {
      const container = this.$el.querySelector('.conversation-wrap');
      container.scrollTop = container.scrollHeight;
    },
    setBubbleLabel() {
      IFrameHelper.sendMessage({
        event: 'setBubbleLabel',
        label: this.$t('BUBBLE.LABEL')
      });
    },
    setLocale(locale) {
      const { enabledLanguages } = this.webChannelConfig;
      if (enabledLanguages.some(lang => lang.iso_639_1_code === locale)) {
        this.$root.$i18n.locale = locale;
      }
    },
    setPosition(position) {
      const widgetPosition = position || 'right';
      this.widgetPosition = widgetPosition;
    },
    setHideMessageBubble(hideBubble) {
      this.hideMessageBubble = !!hideBubble;
    },
    registerUnreadEvents() {
      bus.$on('on-agent-message-recieved', () => {
        if (!this.isIFrame) {
          this.setUserLastSeen();
        }
        this.setUnreadView();
      });
      bus.$on('on-unread-view-clicked', () => {
        this.unsetUnreadView();
        this.setUserLastSeen();
      });
    },
    registerCampaignEvents() {
      bus.$on('on-campaign-view-clicked', async (campaignId) => {
        const { websiteToken } = this.webChannelConfig;
        const { email: currentUserEmail = '' } = this.currentUser;
        this.showCampaignView = false;
        this.showUnreadView = false;
        this.unsetUnreadView();
        this.setUserLastSeen();
        IFrameHelper.sendMessage({ event: 'nudgeClicked' });
        await this.fetchHiverConfig(websiteToken);
        this.$store.dispatch('campaign/campaignMessageClicked', true);
        this.$store.dispatch('campaign/setCampaignTrigger', {type: CAMPAIGN_TRIGGER.NUDGE, id: campaignId});
        if (this.isChatAvailable || this.hasAConnectedAgentBot) {
            if (this.preChatFormEnabled && !currentUserEmail) {
                bus.$on('on-campaign-prechatform-submit', () => {
                    this.startCampaign(campaignId);
                    this.handleChatbotFlowOnNewConversation(campaignId, CAMPAIGN_TRIGGER.NUDGE, !!this.conversationSize);
                    this.clearConversationData(this.conversationSize);
                });
            } else {
               this.startCampaign(campaignId);
                if (this.hasAConnectedAgentBot && !currentUserEmail && !this.preChatFormEnabled && !this.conversationSize) {
                  this.handleChatbotFlowOnNewConversation(campaignId, CAMPAIGN_TRIGGER.NUDGE, !!this.conversationSize);
                  this.clearConversationData(this.conversationSize);
               }
            }
        }
      });
    },
    clearConversationData(conversationSize) {
      if (conversationSize) {
        this.clearConversations();
        this.clearConversationAttributes();
      }
    },
    startCampaign(campaignId) {
        if (this.hasAConnectedAgentBot) return;
        const { websiteToken } = this.webChannelConfig;
        this.executeCampaign({ campaignId, websiteToken });
        this.setConversationsListLoading(true);
        setTimeout(() => {
            this.fetchOldConversations();
            this.$store.dispatch('conversationAttributes/getAttributes').then(() => bus.$emit('on-campaign-ready'));
        }, 1000)
    },
    setPopoutDisplay(showPopoutButton) {
      this.showPopoutButton = showPopoutButton;
    },
    setCampaignView() {
      const { messageCount, activeCampaign } = this;
      const isCampaignReadyToExecute =
        activeCampaign &&
        !isEmptyObject(activeCampaign) &&
        !messageCount &&
        !this.isWebWidgetTriggered;
      if (this.isIFrame && isCampaignReadyToExecute) {
        this.showCampaignView = true;
        IFrameHelper.sendMessage({ event: 'setCampaignMode' });
      }
    },
    setUnreadView() {
      const { unreadMessageCount } = this;
      if (
        this.isIFrame &&
        unreadMessageCount > 0 &&
        this.conversationAttributes.status !== CONVERSATION_STATUS_TYPES.RESOLVED
      ) {
        IFrameHelper.sendMessage({
          event: 'setUnreadMode',
          unreadMessageCount
        });
      }
    },
    unsetUnreadView() {
      if (this.isIFrame) {
        IFrameHelper.sendMessage({ event: 'resetUnreadMode' });
      }
    },

    setupWidget() {
        const { websiteToken } = this.webChannelConfig;
        this.isWidgetFirstOpening = false;

        this.fetchAvailableAgents(websiteToken);
        this.fetchOldConversations().then(() => {
            this.setUnreadView();
            this.setUserLastSeen();
            });
        this.$store.dispatch('contacts/get');
    },

    createWidgetEvents(message) {
      const { eventName } = message;
      const isWidgetTriggerEvent = eventName === 'webwidget.triggered';

      if (isWidgetTriggerEvent) {
        this.$store.dispatch('campaign/campaignMessageClicked', false);
        this.$store.dispatch('campaign/setCampaignTrigger', {type: CAMPAIGN_TRIGGER.WIDGET, id: null});
      }

      if (isWidgetTriggerEvent && this.isWidgetFirstOpening) {
        this.setupWidget()
      }

      this.isWebWidgetTriggered = true;
      if (
        isWidgetTriggerEvent &&
        (this.showUnreadView || this.showCampaignView)
      ) {
        return;
      }
      this.setUserLastSeen();
      this.$store.dispatch('events/create', { name: eventName });
    },
    setupWidgetBeforeFirstOpeningIfConversationIsOpen() {
        if (this.conversationAttributes && this.conversationAttributes.status === CONVERSATION_STATUS_TYPES.OPEN) {
            this.isWidgetFirstOpening = false;
            this.setupWidget()
        }
    },
    registerListeners() {
      const url = new URL(window.location.href);
      let pageUrl = `${url.origin}${url.pathname}`;

      if(IFrameHelper.inIframe()) {
        pageUrl = document.referrer;
      }

      const { websiteToken } = this.webChannelConfig;
      window.addEventListener('message', e => {
        if (!IFrameHelper.isAValidEvent(e)) {
          return;
        }
        const message = IFrameHelper.getMessage(e);
        if (message.event === 'config-set') {
          this.setLocale(message.locale);
          this.setBubbleLabel();
          this.setPosition(message.position);
          this.setPopoutDisplay(message.showPopoutButton);
          this.setHideMessageBubble(message.hideMessageBubble);
          this.$store.dispatch('appConfig/setDocumentWidth', message.documentWidth);
          this.$store.dispatch('appConfig/setWidgetLocationUrl', message.widgetLocationUrl);
        } else if (message.event === 'widget-visible') {
          this.scrollConversationToBottom();
        } else if (message.event === 'inner-document-width') {
           this.$store.dispatch('appConfig/setDocumentWidth', message.documentWidth);
        } else if (message.event === 'change-url') {
          const { referrerURL, referrerHost } = message;
          this.initCampaigns({
            currentURL: referrerURL,
            websiteToken,
            isInBusinessHours: this.isInBusinessHours,
          });
          window.referrerURL = referrerURL;
          bus.$emit(BUS_EVENTS.SET_REFERRER_HOST, referrerHost);
        } else if (message.event === 'toggle-close-button') {
          this.isMobile = message.showClose;
        } else if (message.event === 'on-bubble-close') {
          this.setUserLastSeen();
        } else if (message.event === 'push-event') {
          this.createWidgetEvents(message);
          /*
        } else if (message.event === 'set-label') {
          this.$store.dispatch('conversationLabels/create', message.label);
        } else if (message.event === 'remove-label') {
          this.$store.dispatch('conversationLabels/destroy', message.label);
          */
        } else if (message.event === 'set-user') {
          this.$store.dispatch('contacts/update', message);
        } else if (message.event === 'set-custom-attributes') {
          this.$store.dispatch(
            'contacts/setCustomAttributes',
            message.customAttributes
          );
        } else if (message.event === 'delete-custom-attribute') {
          this.$store.dispatch('contacts/setCustomAttributes', {
            [message.customAttribute]: null
          });
        } else if (message.event === 'set-locale') {
          this.setLocale(message.locale);
          this.setBubbleLabel();
        } else if (message.event === 'set-unread-view') {
          this.showUnreadView = true;
          this.showCampaignView = false;
        } else if (message.event === 'unset-unread-view') {
          this.showUnreadView = false;
          this.showCampaignView = false;
        }
        if(LiveChatGainSightEventMap[message.event]) {
          handleLiveChatGainSight(LiveChatGainSightEventMap[message.event], pageUrl, this.contact);
          if(this.isSurfaceKbEnabled) {
            let tabName;
            if(message.event === "onNudgeClick") {
              tabName = TAB_TYPE.HOME;
              sendGainSightEvent(SURFACE_KB_IN_CHAT_GS_EVENTS.WIDGET_TAB_CLICKED, {tabName});
            } else if (message.event === "onBubbleClick") {
              const presentTab = localStorage.getItem('presentTab');
              const isChatTab = presentTab === TAB_TYPE.CHAT && this.conversationAttributes.status === CONVERSATION_STATUS_TYPES.OPEN;
              if(!isChatTab) {
                tabName = TAB_TYPE.HOME;
                sendGainSightEvent(SURFACE_KB_IN_CHAT_GS_EVENTS.WIDGET_TAB_CLICKED, {tabName});
              }
            }
          }
        }
      });
    },
    sendLoadedEvent() {
      IFrameHelper.sendMessage({
        event: 'loaded',
        config: {
          authToken: this.authToken,
          channelConfig: this.webChannelConfig
        }
      });
    },
    sendRNWebViewLoadedEvent() {
      RNHelper.sendMessage({
        event: 'loaded',
        config: {
          authToken: this.authToken,
          channelConfig: this.webChannelConfig
        }
      });
    },
    async notifyScriptLoaded(websiteToken) {
      try {
        await HiverApi.notifyScriptLoaded(websiteToken);
      } catch (error) {
        sendExceptionToSentry(error);
      }
    }
  }
};
</script>

<style lang="scss">
  @import '~widget/assets/scss/woot.scss';
</style>
